// Subscription Selectors

// Plans
export const selectPlans = (state) => state.subscription?.plans || [];
export const selectPlansLoading = (state) => state.subscription?.plansLoading || false;
export const selectPlansError = (state) => state.subscription?.plansError || null;

// User Subscription
export const selectSubscription = (state) => state.subscription?.subscription || null;
export const selectHasSubscription = (state) => state.subscription?.hasSubscription || false;
export const selectSubscriptionLoading = (state) => state.subscription?.subscriptionLoading || false;
export const selectSubscriptionError = (state) => state.subscription?.subscriptionError || null;

// Status
export const selectStatus = (state) => state.subscription?.status || null;
export const selectStatusLoading = (state) => state.subscription?.statusLoading || false;
export const selectStatusError = (state) => state.subscription?.statusError || null;

// Usage
export const selectUsage = (state) => state.subscription?.usage || null;
export const selectUsageLoading = (state) => state.subscription?.usageLoading || false;
export const selectUsageError = (state) => state.subscription?.usageError || null;

// Payments
export const selectPayments = (state) => state.subscription?.payments || [];
export const selectPaymentsLoading = (state) => state.subscription?.paymentsLoading || false;
export const selectPaymentsError = (state) => state.subscription?.paymentsError || null;

// Checkout
export const selectCheckoutSession = (state) => state.subscription?.checkoutSession || null;
export const selectCheckoutLoading = (state) => state.subscription?.checkoutLoading || false;
export const selectCheckoutError = (state) => state.subscription?.checkoutError || null;

// Portal
export const selectPortalUrl = (state) => state.subscription?.portalUrl || null;
export const selectPortalLoading = (state) => state.subscription?.portalLoading || false;
export const selectPortalError = (state) => state.subscription?.portalError || null;

// Cancel/Reactivate
export const selectCancelLoading = (state) => state.subscription?.cancelLoading || false;
export const selectCancelError = (state) => state.subscription?.cancelError || null;
export const selectReactivateLoading = (state) => state.subscription?.reactivateLoading || false;
export const selectReactivateError = (state) => state.subscription?.reactivateError || null;

// Derived Selectors
export const selectCurrentPlan = (state) => {
  const subscription = selectSubscription(state);
  return subscription?.plan || null;
};

export const selectSubscriptionFeatures = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.features || [];
};

export const selectMaxStreams = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.max_streams || 0;
};

export const selectMaxStorage = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.max_storage_gb || 0;
};

export const selectMaxBandwidth = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.max_bandwidth_gb || 0;
};

// 'active' | 'expiring_soon' | 'canceling' | 'expired' | 'canceled' | null
export const selectSubscriptionState = (state) => selectSubscription(state)?.state || null;

export const selectSubscriptionDaysRemaining = (state) => {
  const d = selectSubscription(state)?.days_remaining;
  return typeof d === 'number' ? d : null;
};

// "Entitled" — subscription is currently usable (not lapsed).
export const selectIsSubscriptionActive = (state) => {
  const sub = selectSubscription(state);
  if (!sub) return false;
  if (typeof sub.is_entitled === 'boolean') return sub.is_entitled;
  return ['active', 'expiring_soon', 'canceling'].includes(sub.state) || sub.status === 'active';
};

// Had a subscription that has now lapsed → show "Renew".
export const selectNeedsRenewal = (state) => {
  const st = selectSubscriptionState(state);
  return st === 'expired' || st === 'canceled';
};

export const selectIsExpiringSoon = (state) => selectSubscriptionState(state) === 'expiring_soon';

export const selectIsSubscriptionCanceled = (state) => {
  const subscription = selectSubscription(state);
  return subscription?.cancel_at_period_end || subscription?.state === 'canceling' || false;
};

export const selectCurrentPeriodEnd = (state) => {
  const subscription = selectSubscription(state);
  return subscription?.current_period_end || null;
};

export const selectUsagePercentage = (state) => {
  const usage = selectUsage(state);
  if (!usage || !usage.usage) return {};
  
  return {
    streams: usage.usage.streams?.percentage || 0,
    storage: usage.usage.storage?.percentage || 0,
    bandwidth: usage.usage.bandwidth?.percentage || 0
  };
};

export const selectIsNearLimit = (state) => {
  const percentages = selectUsagePercentage(state);
  return {
    streams: percentages.streams >= 80,
    storage: percentages.storage >= 80,
    bandwidth: percentages.bandwidth >= 80
  };
};

export const selectPlanById = (state, planId) => {
  const plans = selectPlans(state);
  return plans.find(plan => plan.id === planId) || null;
};

export const selectRecommendedPlan = (state) => {
  const usage = selectUsage(state);
  const plans = selectPlans(state);
  
  if (!usage || !plans.length) return null;
  
  const currentStreams = usage.usage?.streams?.used || 0;
  const currentStorage = usage.usage?.storage?.used || 0;
  
  // Find the smallest plan that meets current needs
  return plans.find(plan => 
    plan.max_streams > currentStreams && 
    plan.max_storage_gb > currentStorage
  ) || plans[plans.length - 1]; // Return highest tier if none match
};

// Monetization Feature Selectors
export const selectAllowAvod = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_avod || false;
};

export const selectAllowSvod = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_svod || false;
};

export const selectAllowAds = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_ads || false;
};

export const selectAllowGoogleAds = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_google_ads || false;
};

export const selectAllowProgrammaticAds = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_programmatic_ads || false;
};

export const selectAllowPreroll = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_preroll || false;
};

export const selectAllowMidroll = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_midroll || false;
};

export const selectAllowPostroll = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.allow_postroll || false;
};

export const selectAdConfig = (state) => {
  const plan = selectCurrentPlan(state);
  return plan?.ad_config || null;
};

export const selectHasMonetizationFeatures = (state) => {
  return (
    selectAllowAvod(state) ||
    selectAllowSvod(state) ||
    selectAllowAds(state) ||
    selectAllowGoogleAds(state) ||
    selectAllowProgrammaticAds(state)
  );
};
